// "Email me a login link" — the magic-link flow's requesting side.
// Used on the login page for devices where nobody remembers their PIN
// or the password isn't saved.
//
// Flow: user enters their account email → BE emails a one-time link →
// THIS device polls /login/magic/status with the requestId it got back.
// When the user opens the email anywhere and confirms, the next poll
// returns a full login payload and this device signs in. The device
// that clicked the email never gets a session.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { trustHeaders } from "./helpers/twoFactorTrust";
import "../styles/forgot-password.css";
import "../styles/login-link.css";

const POLL_INTERVAL_MS = 2500;
const DEFAULT_TTL_SECONDS = 600;

type Step = "email" | "waiting" | "expired";

interface Props {
    className?: string;
    /** Same success handler the PIN login uses — receives the BE login
     *  payload (uid, username, warehouse, permissions, sessionToken,
     *  requirePasswordChange) and performs the redirect. */
    onLoggedIn: (response: any) => void;
}

const LoginLinkButton = ({ className = "", onLoggedIn }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Polling machinery lives in refs so closing the modal (or a
    // re-render) can always tear it down without stale-closure leaks.
    const pollTimerRef = useRef<number | null>(null);
    const pollInFlightRef = useRef(false);
    const deadlineRef = useRef<number>(0);

    const stopPolling = () => {
        if (pollTimerRef.current != null) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollInFlightRef.current = false;
    };

    // Full teardown on unmount.
    useEffect(() => stopPolling, []);

    // Esc closes the modal — matches the rest of the auth modals.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const close = () => {
        stopPolling();
        setOpen(false);
        setStep("email");
        setError(null);
        setSending(false);
    };

    const startPolling = (requestId: string, expiresInSeconds: number) => {
        deadlineRef.current = Date.now() + expiresInSeconds * 1000;
        pollTimerRef.current = window.setInterval(async () => {
            if (Date.now() > deadlineRef.current) {
                stopPolling();
                setStep("expired");
                return;
            }
            // Skip a tick rather than stacking requests on a slow network.
            if (pollInFlightRef.current) return;
            pollInFlightRef.current = true;
            try {
                // The two-factor trust token rides as a header: a GET has no
                // body, and the cross-site FE cannot send the cookie.
                const res = await apiFetch(`/login/magic/status?requestId=${encodeURIComponent(requestId)}`, {
                    method: "GET",
                    headers: trustHeaders(),
                });
                if (!res.ok) return;
                const response = await res.json().catch(() => ({} as any));
                if (response.status === "approved" || response.status === "two_factor") {
                    // "two_factor": the link was accepted but a code is still
                    // owed — the login page shows the step in the card, so
                    // this modal gets out of the way.
                    stopPolling();
                    close();
                    onLoggedIn(response);
                } else if (response.status === "expired") {
                    stopPolling();
                    setStep("expired");
                }
                // "pending" → keep polling.
            } catch {
                // Transient network error — the next tick retries.
            } finally {
                pollInFlightRef.current = false;
            }
        }, POLL_INTERVAL_MS);
    };

    const handleSend = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (sending) return;
        const trimmed = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError(t("login_link_invalid_email"));
            return;
        }
        setSending(true);
        setError(null);
        try {
            const res = await apiFetch(`/login/magic/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: trimmed }),
            });
            const response = await res.json().catch(() => ({} as any));
            if (res.ok && typeof response.requestId === "string") {
                setStep("waiting");
                startPolling(
                    response.requestId,
                    typeof response.expiresInSeconds === "number"
                        ? response.expiresInSeconds
                        : DEFAULT_TTL_SECONDS,
                );
            } else {
                setError(response?.message || t("server_error"));
            }
        } catch {
            setError(t("server_error"));
        } finally {
            setSending(false);
        }
    };

    const restart = () => {
        stopPolling();
        setStep("email");
        setError(null);
    };

    // Portaled to body for the same reason as ForgotPasswordButton —
    // the login step animation leaves a transform on the form, which
    // would trap a position:fixed overlay.
    const modal = (
        <div
            className="forgot-password-overlay"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-link-title"
        >
            <div className="forgot-password-card" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="forgot-password-close"
                    onClick={close}
                    aria-label={t("close")}
                >
                    <X size={18} />
                </button>

                {step === "email" && (
                    <>
                        <h2 id="login-link-title" className="forgot-password-title">
                            {t("login_link_title")}
                        </h2>
                        <p className="forgot-password-body">{t("login_link_body")}</p>
                        <form className="login-link-form" onSubmit={handleSend}>
                            <input
                                className="login-link-input"
                                type="email"
                                autoComplete="email"
                                placeholder={t("login_link_email_placeholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoFocus
                            />
                            {error && <p className="login-link-error">{error}</p>}
                            <button type="submit" className="btn btn-primary btn-full" disabled={sending}>
                                {sending ? t("login_link_sending") : t("login_link_send")}
                            </button>
                        </form>
                    </>
                )}

                {step === "waiting" && (
                    <>
                        <h2 id="login-link-title" className="forgot-password-title">
                            {t("login_link_waiting_title")}
                        </h2>
                        <p className="forgot-password-body">
                            {t("login_link_waiting_body", { email: email.trim() })}
                        </p>
                        <p className="login-link-waiting-hint">
                            <span className="login-link-pulse" aria-hidden />
                            {t("login_link_waiting_hint")}
                        </p>
                    </>
                )}

                {step === "expired" && (
                    <>
                        <h2 id="login-link-title" className="forgot-password-title">
                            {t("login_link_expired_title")}
                        </h2>
                        <p className="forgot-password-body">{t("login_link_expired_body")}</p>
                        <button
                            type="button"
                            className="btn btn-primary btn-full"
                            onClick={restart}
                        >
                            {t("login_link_try_again")}
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <>
            <button
                type="button"
                className={`forgot-password-link ${className}`.trim()}
                onClick={() => setOpen(true)}
            >
                {t("login_link")}
            </button>

            {open && createPortal(modal, document.body)}
        </>
    );
};

export default LoginLinkButton;
